/**
 * B2B-9: a session's analysis must be graded at the engagement's target level
 * before its observation form (SOF) can be built. The API reports a mismatch
 * as `409 { reason: 'needs_level_analysis', detail: {...} }` on add-session and
 * as `needs_level_analysis: true` on session rows.
 */
import type { EngagementLevel, NeedsLevelAnalysisDetail } from '@/types/mentorEngagements';

type Translate = (key: string, params?: Record<string, string | number>) => string;

const LEVELS: readonly EngagementLevel[] = ['acc', 'pcc', 'mcc'];

const asLevel = (value: unknown): EngagementLevel | null =>
  LEVELS.includes(value as EngagementLevel) ? (value as EngagementLevel) : null;

export const levelName = (level: EngagementLevel): string => level.toUpperCase();

/** The structured 409 detail, or null for any other error. */
export function needsLevelAnalysisDetail(error: unknown): NeedsLevelAnalysisDetail | null {
  const candidate = error as { status?: number; detail?: unknown } | null;
  if (candidate?.status !== 409) return null;
  const detail = candidate.detail as Partial<NeedsLevelAnalysisDetail> | undefined;
  if (!detail || typeof detail !== 'object' || detail.code !== 'needs_level_analysis') return null;
  return {
    code: 'needs_level_analysis',
    engagement_level: asLevel(detail.engagement_level),
    instrument_level: asLevel(detail.instrument_level),
  };
}

/** Mentor-facing explanation with level names, or null when the error is unrelated. */
export function needsLevelAnalysisMessage(
  error: unknown,
  targetLevel: EngagementLevel,
  t: Translate,
): string | null {
  const detail = needsLevelAnalysisDetail(error);
  if (!detail) return null;
  const target = levelName(detail.engagement_level ?? targetLevel);
  return detail.instrument_level
    ? t('mentorEngagements.sessions.levelMismatch', {
      analysisLevel: levelName(detail.instrument_level),
      targetLevel: target,
    })
    : t('mentorEngagements.sessions.levelMismatchUnknown', { targetLevel: target });
}
